import * as React from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
  Divider,
  Chip
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import StarIcon from "@mui/icons-material/Star";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {GET, PATCH} from "APIclient";
import properties from "constants/ApplicationConstants";

const API_BASE = "system-config";

const CONFIG_META = {
  "leave.planned.max-per-year": {
    label: "Max Planned Leaves / Year",
    description: "Maximum number of planned leave days an employee can take per year.",
    icon: <AccessTimeIcon sx={{color: "#7c3aed"}} />,
    unit: "days",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.09)",
    fontFamily: properties.fontFamily
  },
  "leave.unplanned.max-per-year": {
    label: "Max Unplanned Leaves / Year",
    description: "Emergency or sick leave days allowed per calendar year.",
    icon: <EventBusyIcon sx={{color: "#dc2626"}} />,
    unit: "days",
    color: "#dc2626",
    bg: "rgba(220,38,38,0.09)",
    fontFamily: properties.fontFamily
  },
  "leave.benefit.after-5-years": {
    label: "Bonus Leave After 5 Years",
    description: "Extra leave days granted after completing 5+ years of service.",
    icon: <StarIcon sx={{color: "#d97706"}} />,
    unit: "days",
    color: "#d97706",
    bg: "rgba(217,119,6,0.09)",
    fontFamily: properties.fontFamily
  }
};

function ConfigCard({config, onSave}) {
  const [editing, setEditing] = React.useState(false);
  const [inputVal, setInputVal] = React.useState(config.value);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const meta = CONFIG_META[config.key] || {
    label: config.key,
    description: config.description || "",
    icon: <SettingsIcon />,
    unit: "",
    color: "#607D8B",
    bg: "rgba(96,125,139,0.09)"
  };

  const handleEdit = () => {
    setInputVal(config.value);
    setError("");
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setError("");
  };

  const handleSave = async () => {
    const num = Number(inputVal);
    if (!inputVal || isNaN(num) || num < 0) {
      setError("Please enter a valid positive number.");
      return;
    }
    setSaving(true);
    try {
      await onSave(config.key, String(Math.round(num)));
      setEditing(false);
    } catch (e) {
      setError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: "0.5px solid",
        borderColor: "rgba(0,0,0,0.08)",
        position: "relative",
        overflow: "visible",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.07)",
          borderColor: "rgba(0,0,0,0.14)"
        }
      }}
    >
      {/* Colored top accent bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          borderRadius: "18px 18px 0 0",
          background: meta.color
        }}
      />

      <CardContent sx={{p: 3, pt: 3.5}}>
        {/* Header row */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "11px",
              background: meta.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            {meta.icon}
          </Box>

          {!editing && (
            <Tooltip title="Edit Value">
              <IconButton
                size="small"
                onClick={handleEdit}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  border: "0.5px solid",
                  borderColor: "rgba(0,0,0,0.1)",
                  color: "#94a3b8",
                  "&:hover": {
                    color: meta.color,
                    borderColor: meta.color,
                    background: meta.bg
                  }
                }}
              >
                <EditIcon sx={{fontSize: 14}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Label */}
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#94a3b8",
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            mb: 0.5,
            mt: 1.5
          }}
        >
          {meta.label}
        </Typography>

        {/* Description */}
        <Typography variant="body2" sx={{color: "#64748b", lineHeight: 1.55, mb: 2}}>
          {meta.description}
        </Typography>

        <Divider sx={{mb: 2, borderColor: "rgba(0,0,0,0.06)"}} />

        {/* Value / Edit area */}
        {editing ? (
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                autoFocus
                type="number"
                size="small"
                value={inputVal}
                onChange={e => {
                  setInputVal(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                error={!!error}
                inputProps={{min: 0}}
                sx={{
                  width: 100,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "9px",
                    fontWeight: 600,
                    fontSize: "1.3rem",
                    fontFamily: "'DM Mono', 'Courier New', monospace",
                    color: meta.color,
                    "& fieldset": {borderColor: meta.color, borderWidth: "1.5px"},
                    "&:hover fieldset": {borderColor: meta.color},
                    "&.Mui-focused fieldset": {borderColor: meta.color}
                  }
                }}
              />

              <Chip
                label={meta.unit}
                size="small"
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#64748b",
                  background: "rgba(0,0,0,0.04)",
                  border: "0.5px solid rgba(0,0,0,0.08)",
                  letterSpacing: "0.3px",
                  textTransform: "uppercase"
                }}
              />

              <Tooltip title="Save (Enter)">
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: meta.color,
                    color: "#fff",
                    "&:hover": {background: meta.color, opacity: 0.85},
                    "&.Mui-disabled": {background: "rgba(0,0,0,0.1)", color: "#fff"}
                  }}
                >
                  <CheckIcon sx={{fontSize: 14}} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Cancel (Esc)">
                <IconButton
                  size="small"
                  onClick={handleCancel}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    border: "0.5px solid rgba(0,0,0,0.1)",
                    color: "#94a3b8",
                    "&:hover": {background: "rgba(0,0,0,0.04)"}
                  }}
                >
                  <CloseIcon sx={{fontSize: 14}} />
                </IconButton>
              </Tooltip>
            </Box>

            {error && (
              <Typography sx={{fontSize: "11px", color: meta.color, mt: 0.75, fontWeight: 500}}>
                {error}
              </Typography>
            )}
          </Box>
        ) : (
          <Box display="flex" alignItems="baseline" gap={0.75}>
            <Typography
              sx={{
                fontSize: "42px",
                fontWeight: 600,
                fontFamily: "'DM Mono', 'Courier New', monospace",
                color: meta.color,
                lineHeight: 1,
                letterSpacing: "-2px"
              }}
            >
              {config.value}
            </Typography>
            <Typography
              sx={{fontSize: "13px", fontWeight: 500, color: "#94a3b8", letterSpacing: "0.3px"}}
            >
              {meta.unit}
            </Typography>
          </Box>
        )}

        {config.updatedAt && !editing && (
          <Typography sx={{fontSize: "11px", color: "#b5bec9", display: "block", mt: 1.5}}>
            Updated {new Date(config.updatedAt).toLocaleString()}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function DefaultValues() {
  const [configs, setConfigs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success"
  });

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await GET(API_BASE);
      setConfigs(response?.data?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({open: true, message: "Failed to load configurations.", severity: "error"});
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchConfigs();
  }, []);

  const handleSave = async (key, value) => {
    try {
      const response = await PATCH(`${API_BASE}/${encodeURIComponent(key)}`, {value});
      const updated = response?.data?.data;
      setConfigs(prev => prev.map(c => (c.key === key ? updated : c)));
      setSnackbar({
        open: true,
        message: "Configuration updated successfully.",
        severity: "success"
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3} px={2}>
        <Box display="flex" alignItems="flex-start" gap={1.75} mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: "linear-gradient(135deg, #1a1a2e, #16213e)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "1px solid rgba(255,255,255,0.08)"
            }}
          >
            <SettingsIcon sx={{color: "#a78bfa", fontSize: 22}} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                color: "#1e293b",
                letterSpacing: "-0.3px",
                lineHeight: 1.2,
                fontFamily: properties.fontFamily
              }}
            >
              System Configurations
            </Typography>
            <Typography
              variant="body2"
              sx={{color: "#64748b", mt: 0.5, fontFamily: properties.fontFamily}}
            >
              Global leave policy settings · Changes apply immediately
            </Typography>
          </Box>
        </Box>

        <Box
          display="grid"
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: "repeat(3, 1fr)"
            },
            gap: 2.5,
            mb: 3
          }}
        >
          {loading
            ? [1, 2, 3].map(i => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{borderRadius: "18px", border: "0.5px solid rgba(0,0,0,0.08)"}}
                >
                  <CardContent sx={{p: 3}}>
                    <Skeleton
                      variant="rectangular"
                      height={40}
                      width={40}
                      sx={{borderRadius: "11px", mb: 2}}
                    />
                    <Skeleton width="55%" height={14} sx={{mb: 1}} />
                    <Skeleton width="85%" height={13} sx={{mb: 0.5}} />
                    <Skeleton width="65%" height={13} sx={{mb: 2}} />
                    <Skeleton width="40%" height={52} sx={{borderRadius: "8px"}} />
                  </CardContent>
                </Card>
              ))
            : configs.map(config => (
                <ConfigCard key={config.key} config={config} onSave={handleSave} />
              ))}
        </Box>

        {!loading && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 2,
              borderRadius: "13px",
              background: "rgba(0,0,0,0.02)",
              border: "0.5px solid rgba(0,0,0,0.07)"
            }}
          >
            <InfoOutlinedIcon sx={{color: "#94a3b8", fontSize: 18, mt: 0.1, flexShrink: 0}} />
            <Typography
              variant="body2"
              sx={{color: "#64748b", lineHeight: 1.55, fontFamily: properties.fontFamily}}
            >
              These values are stored in the database and applied globally. Editing any value here
              will immediately affect all leave calculations, approvals, and eligibility checks
              across the application.
            </Typography>
          </Box>
        )}
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({...s, open: false}))}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        sx={{mt: 6}}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({...s, open: false}))}
          sx={{borderRadius: "12px"}}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
